<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('super-radios-summary')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit Radio</h1>
        </header>

        <section class="module__body">
            <form
                class="full-width"
                @submit.prevent="updateRadio"
            >
                <div class="form-flex">
                    <div class="form-flex-group">
                        <RadioSerialNumberField
                            id="serial_number"
                            v-model="serial_number"
                            data-test-id-prefix="serial-number"
                            label="Serial Number"
                            :radio-type="radioTypes.PRO_X2"
                            :disabled="!isBasicRadio()"
                            :required="true"
                        />

                        <div class="form-group">
                            <label for="architecture">Firmware Architecture</label>
                            <select
                                id="architecture"
                                v-model="architecture"
                                data-test-id="architecture_selector"
                                :disabled="!isBasicRadio()"
                                required
                            >
                                <option
                                    v-for="(arch, index) in architectures"
                                    :key="index"
                                    :value="arch.type"
                                >
                                    {{ arch.label }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label for="purchase_order">Purchase Order</label>
                            <input
                                id="purchase_order"
                                v-model="purchase_order"
                                type="text"
                                required
                                minlength="9"
                                maxlength="9"
                                pattern="^\d{2}(?:0[1-9]|1[0-2])-\d{4}$"
                                data-test-id="purchase_order_input"
                                :disabled="!isBasicRadio()"
                                placeholder="2412-1234"
                            />
                            <div class="help">
                                Purchase Order format is 2 digit year, 2 digit month, hyphen, and 4 digits
                            </div>
                        </div>
                        <div class="form-group">
                            <label>Premium Authorized</label>
                            <slider-toggle-field
                                name="isPremiumAuthorized"
                                label=""
                                :value="is_premium_authorized"
                                :isLocked="!isBasicRadio()"
                                @update="toggleIsPremiumAuthorized"
                            ></slider-toggle-field>
                        </div>
                    </div>
                    <div class="form-flex-group">
                        <div class="form-group">
                            <label>Organization</label>
                            <organization-selector
                                :organizationId="organization_id"
                                :disabled="!isBasicRadio()"
                                data-test-id="organization_selector"
                                @update="organizationUpdated"
                            />
                        </div>
                        <div class="form-group">
                            <label for="customer">Customer</label>
                            <input
                                id="customer"
                                v-model="customer"
                                type="text"
                                minlength="3"
                                maxlength="191"
                                data-test-id="customer_input"
                                :disabled="!isBasicRadio()"
                            />
                        </div>
                        <div class="form-group">
                            <label for="notes">Notes</label>
                            <textarea
                                id="notes"
                                v-model="notes"
                                data-test-id="notes_input"
                                class="short"
                                maxlength="191"
                            />
                        </div>
                    </div>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                        :disabled="!formLoaded"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import RadioSerialNumberField from '@/components/RadioSerialNumberField.vue'
import canToast from '@/mixins/canToast'
import radioTypes from '@/constants/radio-types.js'
import hasBackButton from '@/mixins/hasBackButton'
import SliderToggleField from '@/components/SliderToggleField.vue'
import OrganizationSelector from '@/components/OrganizationSelector.vue'
import radioPremiumStatuses from '@/constants/radio-premium-statuses'
import architectures from '@/constants/firmware-architectures'

export default {
    components: {
        FormErrors,
        SliderToggleField,
        OrganizationSelector,
        RadioSerialNumberField,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            id: this.$route.params.id,
            serial_number: null,
            purchase_order: null,
            customer: null,
            notes: null,
            organization_id: null,
            is_premium_authorized: false,
            premium_status: null,
            architecture: null,
            architectures,
            errorResponse: null,
            formLoaded: false,
            submitted: false,
            radioTypes,
        }
    },

    mounted() {
        this.$http
            .get(`/radios/${this.id}`)
            .then(({ data }) => {
                this.serial_number = data.serial_number
                this.purchase_order = data.purchase_order
                this.customer = data.customer
                this.notes = data.notes
                this.organization_id = Number(data.organization_id)
                this.is_premium_authorized = Boolean(data.is_premium_authorized)
                this.premium_status = data.premium_status
                this.architecture = data.architecture
            })
            .then(() => {
                this.formLoaded = true
            })
    },

    methods: {
        isBasicRadio() {
            return this.premium_status === radioPremiumStatuses.BASIC
        },
        toggleIsPremiumAuthorized(event) {
            if (this.isBasicRadio()) {
                this.is_premium_authorized = event
            }
        },
        updateRadio() {
            this.submitted = true

            this.$http
                .put(`radios/${this.id}`, {
                    serial_number: this.serial_number,
                    purchase_order: this.purchase_order,
                    customer: this.customer,
                    notes: this.notes,
                    organization_id: this.organization_id,
                    is_premium_authorized: Number(this.is_premium_authorized),
                    architecture: this.architecture,
                })
                .then(() => {
                    this.toast('Radio successfully updated!')
                    this.$router.push('/radios')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },

        organizationUpdated(event) {
            this.organization_id = event
        },
    },
}
</script>
